import "./index.css";
import Item from "./Item";
import More from "./More";
import React, { useEffect, useRef, useState } from "react";

import type { TypeGuidelineSelectProps } from "..";
import type { TypeTag } from "../../../hooks/useAllPrograms";

interface TypeTagItemsProps<T = TypeTag["id"]>
  extends Pick<TypeGuidelineSelectProps, "programs"> {
  /**
   * @param isMobile 是否是移动设备
   */
  isMobile: boolean;
  value: T[];
  /**
   * @name onChange 选中 tag 触发变化
   */
  onChange(id: T): void;
  /**
   * @name onAllChange 全选/取消全选
   * @param id 全选的id 是 "-1"，这是前端写死固定的
   * @description 第一个 tag “All”是前端生成的，他的逻辑与 “onChange” 方法略有不同，所以单独出来
   */
  onAllChange(id: -1): void;
}

// 限制在第 几行 ，若元素“超过该行”，则开启 “other” label
const LIMIT_ROW = 2;
// “展开”状态的session key
const KEY_EXPAND = "scenario_tag_expand";

/**
 * @name TagItems tag 选项列表
 * @description tag 与 guideline 是关联关系，一个 ”tag“标签 会关联多个 ”Guideline tag“
 */
const TagItems: React.FC<TypeTagItemsProps> = ({
  isMobile,
  programs,
  onChange,
  value = [],
  onAllChange,
}) => {
  const { loading } = programs;
  const { TAGS, GUIDELINES } = programs.data;

  const ref = useRef<HTMLUListElement>();

  const [index, serIndex] = useState<number>();

  const [expand, setExpand] = useState(
    () => sessionStorage.getItem(KEY_EXPAND) === "true",
  );

  function onExpandChange() {
    const bol = !expand;
    setExpand(bol);
    sessionStorage.setItem(KEY_EXPAND, `${bol}`);
  }

  useEffect(() => {
    if (!TAGS?.length || loading) return;
    const WIDTH_CONTAINER = ref.current?.offsetWidth;
    const group = Array.from(ref.current.children).reduce(
      (v, e: HTMLLIElement) => {
        const row = v[v.length - 1];
        return (
          row && row[0].offsetTop === e.offsetTop ? row.push(e) : v.push([e]), v
        );
      },
      [],
    );
    const WIDTH_TOTAL = group?.[LIMIT_ROW - 1]?.reduce(
      (total: number, e: HTMLLIElement) =>
        total + (e.dataset.name ? e.offsetWidth + 8 : 0),
      0,
    );
    const id =
      group?.[LIMIT_ROW]?.[0]?.dataset?.index -
      (WIDTH_CONTAINER - WIDTH_TOTAL > 26 ? 1 : 2);
    serIndex((i) => (id ? id : i));
  }, [TAGS, loading]);

  // 是否全选
  const IS_ALL = value?.length === 1 && value?.includes(-1);

  return (
    <ul ref={ref} className={`guideline-items${isMobile ? "-mobile" : ""}`}>
      {TAGS?.length ? (
        <>
          <Item
            name="All"
            checked={IS_ALL}
            count={GUIDELINES?.length}
            onClick={() => onAllChange(-1)}
          />
          <li className="guideline-label-separator" />
        </>
      ) : null}
      {TAGS?.map((v, i) => (
        <React.Fragment>
          {isMobile || !(i > index && !expand) ? (
            <Item
              index={i}
              key={v.id}
              name={v.name}
              count={v.documents.length}
              onClick={() => onChange(v.id)}
              checked={IS_ALL ? false : value.includes(v.id)}
            />
          ) : null}
        </React.Fragment>
      ))}

      {isMobile ||
        (index > 0 && (
          <More
            expand={expand}
            onClick={onExpandChange}
            count={TAGS.length - index}
          />
        ))}
    </ul>
  );
};

export default TagItems;
